import { Dispatch, SetStateAction, useEffect, useState } from 'react';

export interface IUseFetch<R> {
  loading: boolean;
  result: R;
  error: unknown;
  setResult: Dispatch<SetStateAction<R>>;
  setLoading: Dispatch<SetStateAction<boolean>>;
}

type TFetcher<T, P extends unknown[]> = (...args: P) => Promise<{ data: T }>;

export default function useFetch<T, P extends unknown[]>(
  fn: TFetcher<T, P>,
  params: P,
  defResult: T,
  ...deps: unknown[]
): IUseFetch<T>;
export default function useFetch<T, P extends unknown[]>(
  fn: TFetcher<T, P>,
  params: P,
  defResult?: Partial<T>,
  ...deps: unknown[]
): IUseFetch<Partial<T>>;
export default function useFetch<T, P extends unknown[]>(
  fn: TFetcher<T, P>,
  params: P,
  defResult: Partial<T> = {},
  ...deps: unknown[]
): IUseFetch<Partial<T>> {
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<Partial<T>>(defResult);
  const [error, setError] = useState<unknown>(null);

  const fetcherWrapper = async () => {
    try {
      const { data } = await fn(...params);
      setResult(data);
      setLoading(false);
    } catch (e) {
      setResult(defResult);
      setLoading(false);
      setError(e);
    }
  };

  useEffect(() => {
    fetcherWrapper();
  }, [...deps]);

  return {
    loading,
    result,
    error,
    setResult,
    setLoading,
  };
}
